/**
 * World wonders (ADR-0057): buildings that exist once in the whole world.
 *
 * Nothing about a wonder is stored apart from the building itself — who holds one is
 * whichever city has it in its building list, so capturing the city captures the
 * wonder and no registry can drift out of step with the map. A wonder's `effects`
 * work like any building's, on its own city; its `civEffects` reach every city its
 * holder owns, and are summed here.
 */

import type { BuildingDef, Content } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import type { City, GameState, Id, ProductionItem } from '../state/types.js';

type CivEffect = 'contentment' | 'productionBonus' | 'goldBonus' | 'sciencePercent' | 'culture';

interface WonderIndex {
  /** Lowest wonder id: city building lists ascend, so anything below it is not a wonder. */
  first: number;
  /** Wonders with civilisation-wide effects. */
  reaching: Set<number>;
}

const indexCache = new WeakMap<Content, WonderIndex>();

function wonderIndex(content: Content): WonderIndex {
  const hit = indexCache.get(content);
  if (hit) return hit;
  const wonders = content.buildings.all.filter((b) => b.wonder);
  const index: WonderIndex = {
    first: wonders.length ? Math.min(...wonders.map((b) => b.id)) : Infinity,
    reaching: new Set(wonders.filter((b) => Object.keys(b.civEffects).length > 0).map((b) => b.id)),
  };
  indexCache.set(content, index);
  return index;
}

export function isWonder(content: Content, item: ProductionItem): boolean {
  return item.kind === 'building' && content.buildings.get(item.building).wonder;
}

/** Every wonder a city has, cheaply: building lists ascend and wonders sit at the end. */
function wondersIn(content: Content, city: City, out: (def: BuildingDef) => void): void {
  const { first } = wonderIndex(content);
  for (let i = city.buildings.length - 1; i >= 0 && city.buildings[i]! >= first; i--) {
    const def = content.buildings.get(city.buildings[i]!);
    if (def.wonder) out(def);
  }
}

/** The city holding a wonder, or null if nobody has built it. */
export function wonderHolder(state: GameState, building: number): City | null {
  for (const city of Object.values(state.cities).sort((a, b) => a.id - b.id)) {
    if (city.buildings.includes(building)) return city;
  }
  return null;
}

/**
 * Why a city cannot start this wonder, or null. Built: someone, anywhere, already has
 * it. Underway: another of this civilisation's cities is building it — one attempt per
 * civilisation, so a wonder never splits a civilisation's own production.
 */
export function wonderBlocker(
  state: GameState,
  content: Content,
  city: City,
  item: ProductionItem,
): 'WonderBuilt' | 'WonderUnderway' | null {
  if (item.kind !== 'building' || !content.buildings.get(item.building).wonder) return null;
  if (wonderHolder(state, item.building)) return 'WonderBuilt';
  for (const other of Object.values(state.cities)) {
    if (other.owner !== city.owner || other.id === city.id) continue;
    if (other.queue.some((q) => q.kind === 'building' && q.building === item.building)) return 'WonderUnderway';
  }
  return null;
}

/** The city of this civilisation building a wonder, if any. */
export function wonderUnderwayIn(state: GameState, civId: Id, building: number): City | null {
  for (const city of Object.values(state.cities).sort((a, b) => a.id - b.id)) {
    if (city.owner !== civId) continue;
    if (city.queue.some((q) => q.kind === 'building' && q.building === building)) return city;
  }
  return null;
}

/** One civilisation-wide effect, summed over every wonder the civilisation holds. */
export function civEffectTotal(state: GameState, content: Content, civId: Id, effect: CivEffect): number {
  const { reaching } = wonderIndex(content);
  if (reaching.size === 0) return 0;
  let total = 0;
  for (const city of Object.values(state.cities)) {
    if (city.owner !== civId) continue;
    wondersIn(content, city, (def) => {
      if (reaching.has(def.id)) total += def.civEffects[effect] ?? 0;
    });
  }
  return total;
}

/** Whether a wonder the civilisation holds makes all its new units veterans. */
export function civTrainsVeterans(state: GameState, content: Content, civId: Id): boolean {
  let found = false;
  for (const city of Object.values(state.cities)) {
    if (city.owner !== civId) continue;
    wondersIn(content, city, (def) => {
      if (def.civEffects.veteranUnits) found = true;
    });
    if (found) return true;
  }
  return false;
}

/** Every wonder in the world, with where it stands, in content order. */
export function wondersBuilt(state: GameState, content: Content): Array<{ def: BuildingDef; city: City }> {
  const out: Array<{ def: BuildingDef; city: City }> = [];
  for (const def of content.buildings.all) {
    if (!def.wonder) continue;
    const city = wonderHolder(state, def.id);
    if (city) out.push({ def, city });
  }
  return out;
}

/**
 * A wonder has been finished: take it out of every other city's queue. Their stored
 * production stays where it is and goes to whatever they build next.
 */
export function purgeWonder(state: GameState, content: Content, building: number, builtIn: City): GameEvent[] {
  const events: GameEvent[] = [];
  const name = content.buildings.get(building).name;
  for (const city of Object.values(state.cities).sort((a, b) => a.id - b.id)) {
    if (city.id === builtIn.id) continue;
    const before = city.queue.length;
    const wasHead = city.queue[0]?.kind === 'building' && city.queue[0].building === building;
    city.queue = city.queue.filter((q) => !(q.kind === 'building' && q.building === building));
    if (city.queue.length !== before) {
      events.push({ t: 'WonderLost', cityId: city.id, name, by: builtIn.owner, stored: wasHead ? city.productionStore : 0 });
    }
  }
  return events;
}
